import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X as XIcon, Send, AlertTriangle } from 'lucide-react';
import { apiService } from '../../../services/apiService';
import type { Patient } from '../../../services/apiService';
import { useToast } from '../../../contexts/ToastContext';

// Schlanker Dialog für freie Mails an den Patienten (Mini-CRM Phase 1,
// docs/CRM-PLAN.md): kind='manual', immer info@-Transport. Bewusst KEIN
// Rich-Text und KEINE Vorlagen — visuelles Muster: billing/SendInvoiceDialog.

interface ComposeMailDialogProps {
    patient: Patient;
    onClose: () => void;
    /** Nach erfolgreichem Versand aufgerufen (Timeline-Reload). */
    onSent?: () => void;
    /** Antwort auf eine Inbound-Mail (Phase 2a): hängt die Antwort in den
     *  Thread und markiert die Inbound-Mail serverseitig als 'answered'. */
    replyToCommunicationId?: number;
    /** Vorbelegung des Empfängers (Default: E-Mail der Patientenakte). */
    initialTo?: string;
    /** Vorbelegung des Betreffs (z. B. "Re: …" bei Antworten). */
    initialSubject?: string;
}

const inputClass =
    'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';
const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

// Sprachen der Patientenakte (preferred_language) — Labels aus dem
// patients-Namespace wiederverwendet (dort sind alle vier gepflegt).
const LANGS: Array<{ value: string; labelKey: string }> = [
    { value: 'de', labelKey: 'patients.detail.langDe' },
    { value: 'en', labelKey: 'patients.detail.langEn' },
    { value: 'es', labelKey: 'patients.detail.langEs' },
    { value: 'ru', labelKey: 'patients.detail.langRu' },
];

const ComposeMailDialog: React.FC<ComposeMailDialogProps> = ({
    patient, onClose, onSent, replyToCommunicationId, initialTo, initialSubject,
}) => {
    const { t } = useTranslation();
    const { addToast } = useToast();

    const [to, setTo] = useState(initialTo ?? (patient.email || ''));
    const [language, setLanguage] = useState<string>(patient.preferred_language || 'es');
    const [subject, setSubject] = useState(initialSubject ?? '');
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Empfänger-Drift (≠ Patientenakte) — rein informativ, blockiert nicht.
    const drift = !!patient.email
        && !!to.trim()
        && to.trim().toLowerCase() !== patient.email.trim().toLowerCase();

    const send = async () => {
        if (!to.trim() || !subject.trim() || !body.trim()) {
            setError(t('crm.compose.requiredFields'));
            return;
        }
        try {
            setSending(true);
            setError(null);
            await apiService.sendCommunication({
                patient_id: patient.id,
                to: to.trim(),
                subject: subject.trim(),
                body,
                language,
                ...(replyToCommunicationId != null ? { reply_to_communication_id: replyToCommunicationId } : {}),
            });
            addToast(t('crm.compose.sent'), 'success');
            onSent?.();
            onClose();
        } catch (e: any) {
            setError(e?.response?.data?.message || t('crm.compose.sendFailed'));
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 px-4 bg-black/50 overflow-y-auto" onClick={onClose}>
            <div className="w-full max-w-2xl rounded-xl bg-brand-surface border border-brand-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-brand-border">
                    <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-brand-text">
                            {t('crm.compose.title', { name: patient.full_name || patient.patient_number })}
                        </h3>
                        <p className="text-xs text-brand-text-muted">{patient.patient_number}</p>
                    </div>
                    <button onClick={onClose} className="p-1 text-brand-text-muted hover:text-brand-text rounded cursor-pointer" aria-label={t('common.close')}>
                        <XIcon className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                </div>

                <div className="p-5 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-2">
                            <label className={labelClass}>{t('crm.compose.toLabel')}</label>
                            <input
                                type="email"
                                value={to}
                                onChange={(e) => setTo(e.target.value)}
                                className={inputClass}
                                placeholder={t('crm.compose.toPlaceholder')}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>{t('crm.compose.languageLabel')}</label>
                            <select value={language} onChange={(e) => setLanguage(e.target.value)} className={inputClass}>
                                {LANGS.map(l => (
                                    <option key={l.value} value={l.value}>{t(l.labelKey)}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {drift && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-md text-xs text-amber-800 dark:text-amber-300">
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                            <span>{t('crm.compose.driftWarning')}</span>
                        </div>
                    )}
                    <div>
                        <label className={labelClass}>{t('crm.compose.subjectLabel')}</label>
                        <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>{t('crm.compose.bodyLabel')}</label>
                        <textarea
                            rows={10}
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            className={inputClass + ' font-mono text-[12.5px] leading-relaxed'}
                        />
                        <p className="mt-1 text-[11px] text-brand-text-muted">
                            {t('crm.compose.signatureHint')}
                        </p>
                    </div>
                    {error && (
                        <div className="px-3 py-2 bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 rounded-md text-xs text-rose-700 dark:text-rose-300">
                            {error}
                        </div>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-brand-border flex items-center justify-end gap-2">
                    <button onClick={onClose} disabled={sending} className="px-3 py-1.5 text-xs font-medium text-brand-text-muted hover:bg-brand-surface-2 rounded cursor-pointer disabled:opacity-50">
                        {t('common.cancel')}
                    </button>
                    <button
                        onClick={send}
                        disabled={sending || !to.trim() || !subject.trim() || !body.trim()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Send className="h-3.5 w-3.5" strokeWidth={2} />
                        {sending ? t('crm.compose.sending') : t('crm.compose.send')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ComposeMailDialog;
